// frontend/src/features/ups/ManageSites.js
// UPS Admin: Manage Sites (control-plane)
// - Loads ALL sites (active + inactive) using loadUpsAdminSites
// - Search + status filter + open drawer + create modal

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  loadUpsAdminSites,
  selectUpsAdminSites,
  selectSitesLoading,
  selectSitesError,
} from '../sites/sitesSlice';

import CreateSiteModal from './modals/CreateSiteModal';
import SiteDrawer from './modals/SiteDrawer';

function normalizeStatus(s) {
  const v = (s ?? '').toString().trim().toLowerCase();
  if (v === 'inactive') return 'inactive';
  return 'active';
}

function StatusBadge({ status }) {
  const st = normalizeStatus(status);
  const cls =
    st === 'active'
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-50 text-gray-700 border-gray-200';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded border ${cls}`}>
      {st}
    </span>
  );
}

export default function ManageSites() {
  const dispatch = useDispatch();
  const sites = useSelector(selectUpsAdminSites);
  const loading = useSelector(selectSitesLoading);
  const error = useSelector(selectSitesError);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);

  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    dispatch(loadUpsAdminSites());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    const st = (statusFilter ?? 'all').toLowerCase();

    return (sites || [])
      .filter((s) => {
        const sStatus = normalizeStatus(s.status);
        if (st === 'all') return true;
        return sStatus === st;
      })
      .filter((s) => {
        if (!q) return true;
        const name = (s.name || '').toLowerCase();
        const code = (s.code || '').toLowerCase();
        return name.includes(q) || code.includes(q);
      });
  }, [sites, query, statusFilter]);

  const openDrawer = (site) => {
    setCurrentSite(site);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setCurrentSite(null);
  };

  const refresh = () => dispatch(loadUpsAdminSites());

  const onCreated = (site) => {
    // refresh list + open drawer for newly created site
    refresh();
    setShowCreate(false);
    if (site) openDrawer(site);
  };

  const totalCount = sites?.length || 0;
  const activeCount = (sites || []).filter((s) => normalizeStatus(s.status) === 'active').length;
  const inactiveCount = totalCount - activeCount;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Sites</h1>
          <div className="text-sm text-gray-500">
            Control-plane list (includes inactive sites). Operational selectors remain active-only.
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            className="border rounded px-3 py-2 text-sm w-64 focus:outline-none focus:ring"
            placeholder="Search sites…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
          >
            New site
          </button>
        </div>
      </div>

      {/* Filter + summary */}
      <div className="bg-white rounded-lg border p-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-sm rounded border transition ${
                statusFilter === 'all' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              All ({totalCount})
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1.5 text-sm rounded border transition ${
                statusFilter === 'active'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setStatusFilter('inactive')}
              className={`px-3 py-1.5 text-sm rounded border transition ${
                statusFilter === 'inactive'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Inactive ({inactiveCount})
            </button>
          </div>

          <div className="text-sm text-gray-600 flex items-center gap-4">
            <button
              onClick={refresh}
              className="px-3 py-1.5 rounded border text-gray-700 hover:bg-gray-50 transition text-sm"
              disabled={loading}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
            {error}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Site</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{s.id}</td>

                  <td className="px-4 py-3">
                    <button onClick={() => openDrawer(s)} className="text-left">
                      <div className="font-semibold text-gray-800">{s.name}</div>
                      <div className="text-xs text-gray-500">
                        {s.created_at ? `Created: ${new Date(s.created_at).toLocaleString()}` : ''}
                      </div>
                    </button>
                  </td>

                  <td className="px-4 py-3 text-gray-700 font-mono text-xs">{s.code || '—'}</td>

                  <td className="px-4 py-3">
                    <StatusBadge status={s.status} />
                  </td>

                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openDrawer(s)}
                      className="px-3 py-1 rounded border text-gray-700 hover:bg-gray-100 transition"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    {query.trim() ? (
                      <>No sites match “{query.trim()}”.</>
                    ) : statusFilter === 'inactive' ? (
                      <>No inactive sites.</>
                    ) : statusFilter === 'active' ? (
                      <>No active sites.</>
                    ) : (
                      <>No sites yet. Create your first site.</>
                    )}

                    <div className="mt-3">
                      <button
                        onClick={() => setShowCreate(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                      >
                        New site
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateSiteModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={onCreated}
        />
      )}

      <SiteDrawer
        isOpen={drawerOpen}
        site={currentSite}
        onClose={closeDrawer}
        onChanged={refresh}
      />
    </div>
  );
}
